import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { app } from '../firebase';
import AuthModal from './AuthModal';
import './Navbar.css';

const auth = getAuth(app);

const Navbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authMode, setAuthMode] = useState('login');
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setUser(firebaseUser);
            // Close user menu when auth state changes
            setShowUserMenu(false);
        });
        return unsubscribe;
    }, []);

    // Close user menu when clicking outside
    useEffect(() => {
        if (!showUserMenu) return;
        
        const handleClickOutside = (e) => {
            const target = e.target;
            const wrapper = document.querySelector('.user-menu-wrapper');
            const dropdown = document.querySelector('.user-menu-dropdown');
            
            // Don't close if clicking inside the wrapper or dropdown
            if ((wrapper && wrapper.contains(target)) || (dropdown && dropdown.contains(target))) {
                return;
            }
            
            // Close if clicking outside
            setShowUserMenu(false);
        };
        
        // Use bubble phase (not capture) so button clicks process first
        document.addEventListener('click', handleClickOutside);
        
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showUserMenu]);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const isActive = (path) => location.pathname === path;

    const handleLogin = () => {
        setAuthMode('login');
        setShowAuthModal(true);
    };

    const handleSignup = () => {
        setAuthMode('signup');
        setShowAuthModal(true);
    };

    const handleAuthSuccess = () => {
        // Show success toast
        showToast(user ? 'Welcome back!' : 'Account created successfully!', 'success');
        // Close mobile menu if open
        setMobileMenuOpen(false);
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            setShowUserMenu(false);
            showToast('Logged out successfully', 'success');
            // Only navigate if not already on home
            if (location.pathname !== '/') {
                navigate('/');
            }
        } catch (error) {
            console.error('Error signing out:', error);
            showToast('Failed to log out. Please try again.', 'error');
        }
    };

    const navLinks = [
        { path: '/', label: 'Home' },
        { path: '/marketplace', label: 'Market' },
        { path: '/pricing', label: 'Pricing' },
        { path: '/upload', label: 'Sell Beats' },
        { path: '/library', label: 'Library' },
    ];

    return (
        <>
            <nav className="navbar">
                <div className="navbar-container">
                    <Link to="/" className="navbar-logo" aria-label="Tune home">
                        <img src="/tune-logo.png" alt="Tune" className="navbar-logo-image" />
                        <span className="navbar-logo-wordmark">Tune</span>
                    </Link>

                    <div className={`navbar-menu ${mobileMenuOpen ? 'active' : ''}`}>
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {link.label}
                            </Link>
                        ))}
                        
                        {user ? (
                            <div className="user-menu-wrapper">
                                <button
                                    className="user-menu-button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowUserMenu(prev => !prev);
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    aria-label="User menu"
                                    aria-expanded={showUserMenu}
                                    type="button"
                                >
                                    {user.photoURL ? (
                                        <img src={user.photoURL} alt={user.displayName || 'User'} className="user-avatar" />
                                    ) : (
                                        <div className="user-avatar-initials">
                                            {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <svg 
                                        width="16" 
                                        height="16" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        strokeWidth="2"
                                        className={showUserMenu ? 'rotated' : ''}
                                    >
                                        <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                </button>
                                {showUserMenu && (
                                    <div className="user-menu-dropdown">
                                        <div className="user-menu-header">
                                            <div className="user-menu-info">
                                                <div className="user-menu-name">
                                                    {user.displayName || user.email?.split('@')[0] || 'User'}
                                                </div>
                                                <div className="user-menu-email">
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            className="user-menu-item"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setShowUserMenu(false);
                                                setMobileMenuOpen(false);
                                                navigate('/profile');
                                            }}
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                            }}
                                            type="button"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                                <circle cx="12" cy="7" r="4"/>
                                            </svg>
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            className="user-menu-item"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setShowUserMenu(false);
                                                setMobileMenuOpen(false);
                                                navigate('/settings');
                                            }}
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                            }}
                                            type="button"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="3"/>
                                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8.92 4a1.65 1.65 0 0 0 1-1.51V2a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09A1.65 1.65 0 0 0 19.4 15z"/>
                                            </svg>
                                            <span>Settings</span>
                                        </button>
                                        <button
                                            className="user-menu-item logout-item"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setShowUserMenu(false);
                                                handleLogout();
                                            }}
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                            }}
                                            type="button"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                                <polyline points="16 17 21 12 16 7"/>
                                                <line x1="21" y1="12" x2="9" y2="12"/>
                                            </svg>
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="auth-buttons">
                                <button className="login-button" onClick={handleLogin}>
                                    Log In
                                </button>
                                <button className="signup-button" onClick={handleSignup}>
                                    Sign Up
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        className="mobile-menu-toggle"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}>
                            <span></span>
                            <span></span>
                            <span></span>
                        </span>
                    </button>
            </div>
        </nav>

            <AuthModal
                isOpen={showAuthModal}
                onClose={() => {
                    setShowAuthModal(false);
                    setAuthMode('login'); // Reset to login mode
                }}
                initialMode={authMode}
                onAuthSuccess={handleAuthSuccess}
            />


            {/* Toast Notification */}
            {toast && (
                <div className={`toast-notification ${toast.type}`}>
                    {toast.type === 'success' ? (
                        <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                        >
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                    ) : (
                        <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                        >
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                    )}
                    <span>{toast.message}</span>
                </div>
            )}
        </>
    );
};

export default Navbar;
